/**
 * Canonical ImportJob data model
 *
 * An ImportJob records a single import: who ran it, when, where its content came
 * from (a CSV upload or a set of PDFs) and the id of every question it created for
 * audit. Rolling a job back soft deletes exactly those questions (skipping any
 * edited since) and marks the job `rolled_back`, so a bad import can be undone and
 * run again
 * @format
 */

import type { Brand } from "./config/brands";

export const IMPORT_JOB_SOURCES = ["csv", "pdf"] as const;

export type ImportJobSource = (typeof IMPORT_JOB_SOURCES)[number];

/**
 * Ordered names of the PDF import state machine. The stage Lambda writes the
 * current name onto the job. The job screen renders these in this order.
 */
export const IMPORT_PIPELINE_STAGES = [
	"ExtractPaper",
	"ExtractMarkScheme",
	"ParseSpecification",
	"Align",
	"AlignBilingual",
	"ExtractAssets",
	"Classify",
	"ExtractKatex",
	"WriteDrafts",
] as const;

export type ImportPipelineStage = (typeof IMPORT_PIPELINE_STAGES)[number];

/** Failure text the classify client stores when the AI platform is still pending. */
export const AI_POLL_TIMEOUT_REASON = "AI job did not complete within the poll timeout";

/**
 * State of one question's AI platform job.
 *
 * `pending` keeps the same request id for the next Classify run. `complete` and
 * `failed` are terminal: Classify does not submit a new prompt for that question.
 */
export const IMPORT_AI_QUESTION_STATES = ["pending", "complete", "failed"] as const;

export type ImportAiQuestionState = (typeof IMPORT_AI_QUESTION_STATES)[number];

/** One stored AI platform job for a question in a PDF import. */
export interface ImportAiQuestion {
	requestId: string;
	state: ImportAiQuestionState;
	/** Set when `state` is `failed`. */
	failedReason?: string;
}

/**
 * Lifecycle states of an import job.
 *
 * - `pending`: an import has claimed the job and is publishing rows.
 * - `running`: a PDF job whose extraction pipeline has started.
 * - `complete`: every row was published.
 * - `failed`: the import stopped at a row. `failedReason` says why.
 * - `cancelled`: a person stopped the import, or the execution was aborted. This is not a failure.
 * - `rolling_back`: a rollback has claimed the job and is publishing deletes.
 * - `rolled_back`: a rollback has finished.
 */
export const IMPORT_JOB_STATUSES = [
	"pending",
	"running",
	"complete",
	"failed",
	"cancelled",
	"rolling_back",
	"rolled_back",
] as const;

export type ImportJobStatus = (typeof IMPORT_JOB_STATUSES)[number];

/**
 * S3 asset keys of the PDFs a PDF import reads, as returned by upload-asset.
 */
export interface ImportSourceKeys {
	/** Key of the question paper PDF */
	paper: string;
	/** Key of the mark scheme PDF */
	markScheme: string;
	/** Key of the specification PDF */
	specification: string;
	/** Key of the Welsh question paper PDF. WJEC only. Absent when the import is English-only. */
	paperCy?: string;
	/** Key of the Welsh mark scheme PDF. WJEC only. Present only together with `paperCy`. */
	markSchemeCy?: string;
}

/**
 * Brand rule for the Welsh PDF keys.
 * Both-or-neither is enforced by the create-import schema. Eduqas has no Welsh.
 */
export const welshPdfKeyIssues = (
	brand: Brand,
	keys: Pick<ImportSourceKeys, "paperCy" | "markSchemeCy">,
): { field: string; message: string }[] => {
	if (brand === "eduqas" && (keys.paperCy || keys.markSchemeCy)) {
		return [{ field: "sourceKeys", message: "Eduqas does not accept a Welsh PDF" }];
	}
	return [];
};

/**
 * Progress counts of a PDF import, one number per pipeline stage.
 */
export interface ImportJobCounts {
	/** Questions the pipeline extracted from the PDFs */
	extracted: number;
	/** Extracted questions whose classification was accepted */
	accepted: number;
	/** Questions moved to `staged` */
	staged: number;
	/** Staged questions that have been published */
	published: number;
}

/**
 * One specification statement the review screen can search.
 * Labels are display text, the same strings the classifier proposes.
 */
export interface ImportTaxonomyEntry {
	theme: string;
	topic: string;
	subTopic: string;
}

/**
 * A question a batch write refused to overwrite, because it is already staged or published.
 */
export interface ImportJobCollision {
	/** Id of the question the import tried to write */
	questionId: string;
	/** Status of the existing record, which blocked the overwrite */
	existingStatus: "staged" | "published";
}

/**
 * One persisted record per import.
 */
export interface ImportJob {
	/** UUID assigned when the import is received */
	id: string;
	brand: Brand;
	status: ImportJobStatus;
	/**
	 * Where the job's content came from. Absent on jobs recorded before PDF import
	 * existed, treat absent as `csv`.
	 */
	source?: ImportJobSource;
	/** S3 asset keys of the PDFs a PDF job reads. Absent on CSV jobs. */
	sourceKeys?: ImportSourceKeys;
	/**
	 * ARN of the Step Functions execution that extracts the PDFs. Set after the job
	 * is created, absent on CSV jobs.
	 */
	executionArn?: string;
	/**
	 * How far the extraction pipeline has got. Zeros when a PDF job is created and
	 * updated by the pipeline as it runs. Absent on CSV jobs.
	 */
	counts?: ImportJobCounts;
	/**
	 * The stage that is running, or the stage that was running when the job
	 * failed. Absent on CSV jobs and on PDF jobs recorded before stages were stored.
	 */
	pipelineStage?: ImportPipelineStage;
	/**
	 * Question numbers a stage has produced. A stage is absent until it has
	 * recorded some. Classify grows as each question finishes.
	 */
	stageQuestionNumbers?: Partial<Record<ImportPipelineStage, string[]>>;
	/**
	 * AI platform job for each question number. Absent until Classify submits.
	 * A later Classify run reads these ids and does not submit again.
	 */
	aiQuestions?: Record<string, ImportAiQuestion>;
	/** Name of the uploaded CSV, when the client supplied it */
	fileName?: string;
	/**
	 * The Paper every question in the job belongs to. Optional so jobs recorded
	 * before it was added stay valid.
	 */
	paperId?: string;
	/**
	 * Subject of the job's paper, as shown on the import preview. A display hint
	 * for the history list.
	 */
	subject?: string;
	/**
	 * Specification statements for this job, so the reviewer can search past the
	 * top three proposals. Absent until the classify stage has run.
	 */
	taxonomy?: ImportTaxonomyEntry[];
	/**
	 * Commit hash of the classification prompt used for this job's inference. Metrics
	 * are attributed to it so accuracy can be traced to a prompt version.
	 */
	promptCommit?: string;
	/** Paper code of the job's paper, a display hint */
	paperCode?: string;
	/**Number of rows in the upload */
	rowCount: number;
	/** Identifier of the user that ran the import */
	createdBy: string;
	createdByEmail?: string;
	/** timestamp recording when the import was received */
	createdAt: string;
	/** timestamp recording when every row had been published */
	completedAt?: string;
	/**
	 * Why the job stopped early. Set when `status` is `failed`, or when a rollback
	 * stopped partway (the status is then left unchanged so the rollback can run again).
	 * A cancelled job has no failure reason.
	 */
	failedReason?: string;
	/**
	 * Ids of the questions this job created, in row order. This is the audit trail
	 * a rollback works from.
	 */
	createdQuestionIds: string[];
	/** timestamp recording when a rollback claimed the job (`rolling_back`) or finished (`rolled_back`) */
	rolledBackAt?: string;
	/** Identifier of the user that rolled the job back. */
	rolledBackBy?: string;
	/**
	 * Questions a rollback left in place: edited or published since the import, or
	 * not yet written by the index worker. A non-empty list lets the rollback run again.
	 */
	rollbackSkippedIds?: string[];
	collisions?: ImportJobCollision[];
}

/**
 * The fields the extraction pipeline may update on an existing job. Every field
 * is optional, only the ones present are written.
 *
 * `createdQuestionIds` is the full list of questions the pipeline has created so
 * far, not an addition to the stored list: a write replaces what is stored. It is the
 * audit trail a rollback works from, with the same meaning as on a CSV job.
 */
export type ImportJobProgress = Partial<
	Pick<
		ImportJob,
		| "status"
		| "pipelineStage"
		| "stageQuestionNumbers"
		| "aiQuestions"
		| "executionArn"
		| "counts"
		| "createdQuestionIds"
		| "collisions"
		| "failedReason"
		| "completedAt"
		| "taxonomy"
	>
>;

/**
 * How long a CSV job may sit `pending` before it is treated as abandoned. Four
 * times the create Lambda's 30 s timeout, so a genuinely in-flight import
 * can never be rolled back underneath itself.
 */
export const STALE_PENDING_MS = 120_000;

/**
 * Decides whether an import job may be rolled back. The rollback endpoint
 * and the admin details page both apply this rule, so it lives here to keep the two
 * from drifting.
 *
 * A failed import can be rolled back only when it created questions. That is a
 * partial import. A failed import that created nothing has nothing to undo.
 * A pending job inside the stale window is still running, so it cannot be rolled
 * back. A pending CSV job left stale can be rolled back only when it created
 * questions. A pending PDF job is only waiting for its pipeline to start, so it
 * is never rolled back. A job without a `source` is a CSV job.
 *
 * A running job has its pipeline in progress and is never rolled back.
 *
 * A rolling-back job has a rollback in flight. If that rollback's Lambda timed out,
 * the job never leaves `rolling_back`, so once the claim is older than
 * {@link STALE_PENDING_MS} it can be rolled back again. The claim time is
 * `rolledBackAt`, not `createdAt`: the job may be old, but the claim is not.
 *
 * A rolled-back job that kept some questions can be rolled back again. A kept
 * question may only have been not yet written when the first rollback ran.
 *
 * A complete job can be rolled back for either source. A failed or cancelled job
 * can be rolled back only when it created questions. A rolled-back job follows the
 * kept-question rule for either source.
 *
 * @param job - The import job to check.
 * @param now - Current time in epoch milliseconds. Defaults to `Date.now()`.
 * @returns `true` for:
 * - a complete job;
 * - a failed or cancelled job that created questions;
 * - a pending CSV job older than {@link STALE_PENDING_MS} that created questions;
 * - a rolling-back job whose claim is older than {@link STALE_PENDING_MS};
 * - a rolled-back job with kept questions.
 *
 * A pending job with no questions, a failed job with no questions, a pending PDF
 * job, and a running job return `false`.
 */
const hasCreatedQuestions = (job: ImportJob): boolean => job.createdQuestionIds.length > 0;

export const canRollBackImportJob = (job: ImportJob, now = Date.now()): boolean => {
	if (job.status === "pending") {
		const source = job.source ?? "csv";
		const isStale = source === "csv" && now - Date.parse(job.createdAt) > STALE_PENDING_MS;
		return isStale && hasCreatedQuestions(job);
	}
	if (job.status === "rolling_back") {
		return now - Date.parse(job.rolledBackAt ?? job.createdAt) > STALE_PENDING_MS;
	}
	if (job.status === "rolled_back") {
		return (job.rollbackSkippedIds?.length ?? 0) > 0;
	}
	if (job.status === "failed" || job.status === "cancelled") {
		return hasCreatedQuestions(job);
	}
	return job.status === "complete";
};

/**
 * An import can be cancelled only while it is still in progress.
 * `pending` and `running` are in progress. A finished job cannot be cancelled.
 */
export const canCancelImportJob = (job: ImportJob): boolean => job.status === "pending" || job.status === "running";
